import { Modal } from '../../../../../components/Modal/Modal'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { CloseButton, Col, Container, Row, Stack } from 'react-bootstrap'
import {
  CustomDropdownItemOption,
  DropDownItemOptionComponentProps,
  SelectDropdown,
  SelectOption,
} from '../../../../../components/SelectDropdown/SelectDropdown'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useGetExternalUsersQuery } from '../../../../../store/externalUser/externalUser'
import { getUserDisplayName } from '../../common/aifeatures/utils'
import { UserTag } from '../../UserTag'
import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { useAddAllocationContextUsersMutation } from '../../../../../store/media/media'
import { ASSIGNMENT_ROLE_NAME_MAPPING } from '../../MeetingSummary/ConfigureAssignment/AssignmentRoleNameMapping'
import { TagElement } from '../../../../../components/Tag/Tag'
import { UserAllocationContextUser } from '../../../../../models/Allocation'

export interface AddParticipantModalProps {
  show: boolean
  existingClients: UserAllocationContextUser[]
  onCancel: () => void
  onSuccess: () => void
  polling?: boolean
}

export const AddParticipantModal: React.FC<AddParticipantModalProps> = ({ show, onCancel, onSuccess, existingClients, polling }) => {
  const accountId = useAppSelector(selectAccountId)
  const roomSession = useAppSelector(selectRoomSession)
  const [searchInput, setSearchInput] = useState<string>('')
  const [contactValues, setContactValues] = useState<SelectOption[]>([])
  const [selectedClients, setSelectedClients] = useState<SelectOption[]>([])
  const [addAllocationContextUsers, { isLoading: addingAllocationContextUser, isSuccess: addedAllocationContextUser, reset }] =
    useAddAllocationContextUsersMutation()
  const { data: contacts, isFetching } = useGetExternalUsersQuery(
    { accountId, name: searchInput, pageRequest: { page: 0, size: 500 } },
    { refetchOnMountOrArgChange: true },
  )
  const clientRoleIds = useMemo(
    () =>
      Object.entries(ASSIGNMENT_ROLE_NAME_MAPPING)
        .filter(([key, value]) => value === 'Clients')
        .map(([key, value]) => key),
    [],
  )

  useEffect(() => {
    if (isFetching || !contacts) {
      return
    }

    const clients = contacts.data
      .filter((u) => !existingClients.find((allocationUser) => allocationUser.user.id === u.id))
      .filter((u) => u.roles.some(({ id }) => clientRoleIds.includes(id)))
      .map((u) => ({ id: u.id, label: u.name || getUserDisplayName(u) }))
    setContactValues(clients)
  }, [isFetching, contacts, clientRoleIds, existingClients])

  useEffect(() => {
    if (addedAllocationContextUser) {
      reset()
      onSuccess()
    }
  }, [addedAllocationContextUser, onSuccess, reset])

  const handleSearchInputChange = async (searchInput: string) => {
    setSearchInput(searchInput)
  }

  const handleOptionSelected = (option: SelectOption) => {
    addClient(option)
  }

  const removeClient = (removeClient: SelectOption) => {
    setSelectedClients([...selectedClients.filter((client) => client.id !== removeClient.id)])
  }

  const addClient = (addClient: SelectOption) => {
    if (selectedClients.find((option) => option.id === addClient.id)) {
      return
    }
    setSelectedClients([...selectedClients, addClient])
  }

  const AllocationUserDropdownItemOption: React.FC<DropDownItemOptionComponentProps> = useCallback(({ optionLabel, onClick }) => {
    return (
      <CustomDropdownItemOption role='button' onClick={onClick} className='d-flex gap-2 align-items-center text-truncate'>
        <UserTag userType='external' className='user-tag-small' withoutStyle />
        {optionLabel}
      </CustomDropdownItemOption>
    )
  }, [])

  const ModalBody = (
    <Container className='d-flex flex-column gap-4'>
      <Row>
        <Col className='p-0'>
          <div className='paragraph-medium'>Add clients to generate personalized meeting prep materials.</div>
        </Col>
      </Row>
      <Row>
        <SelectDropdown
          clearDropdownOnOptionSelected={true}
          initialSelectedOption={null}
          options={contactValues}
          className='py-2 zocks-px-0.75 zocks-gap-3'
          onOptionSelected={handleOptionSelected}
          placeholder='Select Contact'
          DropDownItemOptionComponent={AllocationUserDropdownItemOption}
          searchable
          onSearchInputChange={handleSearchInputChange}
        />
      </Row>
      <Row>
        <Stack direction='horizontal' gap={2} className='flex-wrap inline-flex'>
          {selectedClients.map((client) => {
            return (
              <TagElement key={client.id}>
                {client.label} <CloseButton className='user-close-btn' onClick={() => removeClient(client)} />
              </TagElement>
            )
          })}
        </Stack>
      </Row>
      <Row>
        <Col className='p-0'>
          <div className='paragraph-medium'>
            <strong>Want to add Teammates?</strong> You don’t need to. They will be automatically detected and added during the meeting.
          </div>
        </Col>
      </Row>
    </Container>
  )

  const onSave = async () => {
    if (!roomSession || !selectedClients.length) {
      return
    }
    addAllocationContextUsers({
      accountId,
      roomId: roomSession.roomId,
      sessionId: roomSession.id,
      body: selectedClients.map((option) => ({ userId: option.id, name: option.label })),
    })
  }

  return (
    <Modal
      className='add-participant-modal'
      show={show}
      onHide={onCancel}
      header={<h4>Add Client</h4>}
      body={ModalBody}
      showFooterActions={true}
      onFooterPrimaryAction={onSave}
      footerPrimaryActionTitle='Add'
      footerPrimaryActionButtonVariant='primary'
      footerPrimaryActionLoading={addingAllocationContextUser || polling}
      footerPrimaryActionDisabled={!selectedClients.length}
      onFooterSecondaryAction={onCancel}
      centered={true}
    />
  )
}
